<template>
  <div>
    <div class="d-flex flex-column gr-md-8 gr-sm-7 gr-6 registration-form-content">
      <DoctorCard
        v-if="isDoctor && subject"
        :doctor="subject"
        :class="{ 'elevation-1 pa-4': !isMobile }"
      />
      <template v-if="!isFinished">
        <div
          v-if="!isMobile"
          class="text-h5 text-center"
        >
          {{ t(isLpu ? 'Для регистрации клиники заполните форму' : 'Для регистрации врача заполните форму', options) }}
        </div>

        <div class="d-flex flex-column gr-4">
          <UserForm
            ref="userFormRef"
            class="mb-4"
            :is-doctor="isDoctor"
            :is-lpu="isLpu"
            :email-error-message="emailErrorMessage"
            :is-alert-foreign-email-visible="isAlertForeignEmailVisible"
            @reset-email-error="resetEmailError"
          />

          <div
            v-if="isVisibleAgreementYdoc || isLpu || (isDoctor && phoneConfirmationOnDoctorRegister)"
            class="d-flex flex-column gr-4"
          >
            <RegistrationFormAgreement
              v-if="isVisibleAgreementYdoc"
              v-model="isAgreeWithAgreement"
              :class="isButtonSendVisible ? 'order-1' : 'order-0'"
              :is-lpu="isLpu"
              :is-doctor="isDoctor"
              :registration-agreement-link="registrationAgreementLink"
            />

            <div
              v-if="isLpu || (isDoctor && phoneConfirmationOnDoctorRegister)"
              class="registration-form-content__user-authorization mb-4"
              :class="{ 'registration-form-content__user-authorization--with-btn': !isButtonSendVisible }"
            >
              <component
                :is="UserAuthorization"
                v-if="UserAuthorization"
                :key="userAuthorizationKey"
                persistent
                is-no-registration
                :is-country-authorization="true"
                :hook-before-submit-form="beforeCodeRequest"
                @authorization:confirmed="userPhone = $event"
              />
            </div>
          </div>

          <div class="d-flex flex-column gr-4">
            <v-btn
              v-if="isButtonSendVisible"
              block
              elevation="0"
              color="primary"
              :disabled="!isAgreeWithAgreement"
              @click="sendForm"
            >
              {{ t('Отправить', options) }}
            </v-btn>

            <PolicyAndTermsYdoc
              v-if="isVisibleAgreementYdoc"
              :is-button-send-visible="isButtonSendVisible"
            />
            <PolicyAndTerms
              v-else
              v-model="isAgreeWithAgreement"
            />
          </div>
        </div>
      </template>

      <ScreenConfirmEmail
        v-else
        :user-email="userEmail"
        :date-of-sending-email="dateOfSendingEmail"
      />
    </div>

    <ErrorDialog
      v-model="isAlertErrorVisible"
      :error="alertError"
    />

    <CoreDialog
      v-model="isProgressVisible"
      data-qa="registration-form-progress-modal"
      size="sm"
      persistent
    >
      <v-progress-circular
        class="mt-3 mb-6 mx-auto"
        size="40"
        width="3"
        color="primary"
        indeterminate
      />
      <div class="text-body-1 text-center mb-2">
        {{ t('Подождите', options) }}...
      </div>
    </CoreDialog>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, onMounted, ref, shallowRef,
} from 'vue'
import I18NextVue from 'i18next'

import type { ContextStore } from 'src/components/pages/profile/doctors/register/index/types'
import useContextStore from 'src/store/useContextStore'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import useYandexGoals from 'src/composables/useYandexGoals'
import getOnlyNumbers from 'src/utils/getOnlyNumbers'

import type {
  ApiProfileRegisterPostParams,
} from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/api/apiProfileRegister.types'
import apiProfileRegister from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/api/apiProfileRegister'
import useAgreementYdoc from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/composables/useAgreementYdoc'

import DoctorCard from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/DoctorCard.vue'
import ErrorDialog from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/ErrorDialog.vue'
import PolicyAndTermsYdoc from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/PolicyAndTermsYdoc.vue'
import PolicyAndTerms from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/PolicyAndTerms.vue'
import ScreenConfirmEmail from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/ScreenConfirmEmail.vue'
import RegistrationFormAgreement from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/RegistrationFormAgreement.vue'
import UserForm from 'src/components/pages/profile/doctors/register/index/DoctorsRegisterContent/components/RegistrationForm/components/UserForm.vue'

const props = defineProps<{
  phoneConfirmationOnDoctorRegister: boolean
  dateOfSendingEmail?: string | false
  userEmail?: string
  isDoctor: boolean
  isLpu: boolean
  subject: ContextStore['context']['subject']
}>()

const { t, language } = I18NextVue
const options = { ns: language }

const { context }: ContextStore = useContextStore()
const isMobile = computed(() => context.flavour === 'mobile')

const UserAuthorization = shallowRef<any>()

onMounted(async () => {
  if (typeof document !== 'undefined') {
    // TODO: заменить на версию для серверного рендера
    UserAuthorization.value = (await import(/* webpackMode: 'eager' */ 'www/vue_components/common/UserAuthorization/UserAuthorization.vue')).default
  }
})

const isFinished = ref(!!props.dateOfSendingEmail)
const userEmail = ref(props.userEmail)

const isProgressVisible = ref(false)
const isSendingRequest = ref(false)
const isYDoc = computed(() => !context.request.country.is_russia)

const isAlertErrorVisible = ref(false)
const alertError = shallowRef<{
  code: string
  message?: string
}>({
  code: '',
})

function showError(error: { code: string, message?: string }) {
  isAlertErrorVisible.value = true
  alertError.value = error
}

const {
  isVisibleAgreementYdoc,
  registrationAgreementLink,
} = useAgreementYdoc({
  countryIso: computed(() => context.request.country.iso),
  isDoctor: computed(() => props.isDoctor),
  isLpu: computed(() => props.isLpu),
  isYdoc: computed(() => isYDoc.value),
})
const isAgreeWithAgreement = ref(false)
const userPhone = ref('')

const { sendYaGoal } = useYandexGoals()

const userFormRef = ref<InstanceType<typeof UserForm>>()
const isButtonSendVisible = computed(() => Boolean(
  (
    (!props.phoneConfirmationOnDoctorRegister && !props.isLpu)
    || userPhone.value
  )
  && !isFinished.value,
))

const userAuthorizationKey = ref(0)
const emailErrorMessage = ref('')
const isAlertForeignEmailVisible = ref(false)
let requestId = 0

function resetEmailError() {
  emailErrorMessage.value = ''
  isAlertForeignEmailVisible.value = false
}

async function sendForm() {
  if (!userFormRef.value) {
    return
  }

  if (!await userFormRef.value.validate()) {
    showError({ code: 'validationError' })
    return false
  }

  const formData: ApiProfileRegisterPostParams = {
    ...userFormRef.value.userData,
    is_agree_with_agreement: isAgreeWithAgreement.value,
  }

  if (props.isLpu || (props.isDoctor && props.phoneConfirmationOnDoctorRegister)) {
    const countryCodeLength = getOnlyNumbers(context.request.country.phone_code)?.length
    const phoneWithoutCountryCode = getOnlyNumbers(userPhone.value)?.slice(countryCodeLength)

    formData.phone = phoneWithoutCountryCode
  }

  isSendingRequest.value = true
  setTimeout(() => {
    isProgressVisible.value = isSendingRequest.value
  }, 200)

  requestId++

  const currentRequestId = requestId

  try {
    const { data } = await apiProfileRegister.post(formData)

    if (props.isLpu) {
      sendYaGoal('send_form_your_page')
    }

    if (props.isDoctor) {
      sendYaGoal('send_form_this_is_you')
    }

    if (requestId !== currentRequestId) {
      return
    }

    const { email } = data

    if (email) {
      userEmail.value = email
      isFinished.value = true
    } else {
      if (data === 'Запрещена регистрация на email клиники.') {
        showError({ code: 'error_lpu_email' })

        return
      }

      showError({ code: 'defaultError' })
    }
  } catch (error: any) {
    if (requestId !== currentRequestId) {
      return
    }

    const { response, message } = error

    if (response.data === 'error_conflict_phone') {
      userAuthorizationKey.value++
      // eslint-disable-next-line require-atomic-updates
      userPhone.value = ''
    } else if (response.data === 'error_conflict_email') {
      emailErrorMessage.value = t('Адрес уже занят', options)
    } else if (response.data === 'error_foreign_email') {
      isAlertForeignEmailVisible.value = true
    }

    showError({ code: response.data, message })
  } finally {
    isSendingRequest.value = false
    isProgressVisible.value = false
  }
}

async function beforeCodeRequest() {
  if (!await userFormRef.value?.validate()) {
    showError({ code: 'validationError' })
    return false
  }

  return true
}
</script>

<style lang="scss" scoped>
.registration-form-content {
  max-width: 480px;
  margin: 0 auto;

  & :deep(.v-text-field .v-text-field__details) {
    margin-bottom: -20px;
  }

  &__user-authorization {
    min-height: 56px;

    &--with-btn {
      min-height: 138px;
    }

    & :deep(.authorization-form) {
      display: flex;
      flex-direction: column;
      row-gap: 32px;
    }
  }
}

.gr-inherit {
  row-gap: inherit;
}
</style>
